import { atom } from 'nanostores';
import { v4 as uuidv4 } from 'uuid';
import { uploadAssetToGCS } from '~/lib/utils/assetClientUpload';
import type { AssetType } from '~/types/asset';
import type { AssetUploads } from '~/services/dbSchema';
import { ALLOWED_ASSET_IMAGE_EXTENSIONS, MAX_PROJECT_ASSETS } from '~/utils/constants';
import { removePendingAssetJobIds } from './assetUploadPendings';

function isAllowedAssetImageFile(file: File): boolean {
  const name = (file.name || '').toLowerCase();
  return ALLOWED_ASSET_IMAGE_EXTENSIONS.some((ext) => name.endsWith(ext));
}

export interface ProjectAsset {
  id: string;
  projectId: string;
  jobId: string;
  fileName: string;
  size: number;
  status: 'uploading' | 'processing' | 'ready' | 'error';
  createdAt: string;
  isReferenced: boolean;
  file?: File;
  gcsUrl?: string;
  error?: string;
  uploadProgress?: number;
  mimeType?: string;
}

export const projectAssetsStore = atom<ProjectAsset[]>([]);
export const areAssetsLoading = atom(false);
export const isAssetListPopupOpen = atom(false);

const uploadControllers = new Map<string, AbortController>();

export function openAssetListPopup() {
  isAssetListPopupOpen.set(true);
}

export function closeAssetListPopup() {
  isAssetListPopupOpen.set(false);
}

export function toggleAssetListPopup() {
  isAssetListPopupOpen.set(!isAssetListPopupOpen.get());
}

export async function addProjectAssets(files: File[], projectId: string): Promise<void> {
  const current = projectAssetsStore.get();

  if (current.length + files.length > MAX_PROJECT_ASSETS) {
    console.error('[assets] Limit exceeded. Max ${MAX_PROJECT_ASSETS} assets allowed.');
    throw new Error(`Limit exceeded. Max ${MAX_PROJECT_ASSETS} assets allowed.`);
  }

  const invalid = files.filter((f) => !isAllowedAssetImageFile(f));

  if (invalid.length > 0) {
    const invalidNames = invalid.map((f) => f.name).join(', ');
    throw new Error(
      `Invalid file type. Only ${ALLOWED_ASSET_IMAGE_EXTENSIONS.join(', ')} are allowed. Invalid: ${invalidNames}`,
    );
  }

  const newAssets: ProjectAsset[] = files.map((file) => ({
    id: uuidv4(),
    projectId,
    jobId: uuidv4(),
    isReferenced: true,
    file,
    fileName: file.name,
    mimeType: file.type,
    size: file.size,
    status: 'uploading',
    uploadProgress: 0,
    createdAt: new Date().toISOString(),
  }));

  projectAssetsStore.set([...current, ...newAssets]);
  updateLoadingState();

  try {
    await Promise.all(
      newAssets.map((asset) => {
        const controller = new AbortController();
        uploadControllers.set(asset.id, controller);
        return uploadAsset(asset, controller.signal);
      }),
    );
  } catch (error) {
    console.error('[assets] Failed to upload assets:', error);
    throw error;
  }
}

export function removeProjectAsset(id: string): void {
  const current = projectAssetsStore.get();
  projectAssetsStore.set(current.filter((a) => a.id !== id));

  // Abort ongoing upload if any
  const controller = uploadControllers.get(id);

  if (controller) {
    controller.abort();
    uploadControllers.delete(id);
  }

  updateLoadingState();

  deleteAssets([id]);
}

export function clearProjectAssets(): void {
  const current = projectAssetsStore.get();
  const currJobIds = current.map((asset) => asset.jobId);

  // Abort all ongoing uploads
  uploadControllers.forEach((controller) => controller.abort());
  uploadControllers.clear();

  deleteAssets(currJobIds);

  projectAssetsStore.set([]);
  updateLoadingState();
}

export async function toggleAssetReference(jobId: string): Promise<void> {
  const current = projectAssetsStore.get();
  projectAssetsStore.set(current.map((a) => (a.jobId === jobId ? { ...a, isReferenced: !a.isReferenced } : a)));

  try {
    const response = await fetch('/api/asset-reference', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ jobIds: [jobId] }),
    });

    if (!response.ok) {
      throw new Error('Failed to toggle asset reference');
    }
  } catch (error) {
    console.error('[assets] Failed to toggle asset reference:', error);
  }
}

export async function attachAllAssets(): Promise<void> {
  const current = projectAssetsStore.get();
  const toAttach = current.filter((a) => !a.isReferenced);

  if (toAttach.length === 0) {
    return;
  }

  projectAssetsStore.set(current.map((a) => ({ ...a, isReferenced: true })));

  const jobIds = toAttach.map((a) => a.jobId);

  try {
    const response = await fetch('/api/asset-reference', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ jobIds, isReferenced: true }),
    });

    if (!response.ok) {
      throw new Error('Failed to attach all assets');
    }
  } catch (error) {
    console.error('[assets] Failed to attach assets:', error);
  }
}

export async function detachAllAssets(): Promise<void> {
  const current = projectAssetsStore.get();
  const toDetach = current.filter((a) => a.isReferenced);

  if (toDetach.length === 0) {
    return;
  }

  projectAssetsStore.set(current.map((a) => ({ ...a, isReferenced: false })));

  const jobIds = toDetach.map((a) => a.jobId);

  try {
    const response = await fetch('/api/asset-reference', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ jobIds, isReferenced: false }),
    });

    if (!response.ok) {
      throw new Error('Failed to detach assets');
    }
  } catch (error) {
    console.error('[assets] Failed to detach all assets:', error);
  }
}

export function setAssetsFromDB(assets: AssetUploads[]): void {
  const formattedAssets: ProjectAsset[] = assets.map((asset) => {
    const extension = asset.originalObjectName?.split('.').pop() || '';
    const rawName = asset.finalFileName || asset.originalObjectName || 'asset';

    const parts = rawName.split('/');
    const lastPart = parts[parts.length - 1];
    const baseName = lastPart
      .replace(/-[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/i, '')
      .replace(/\.[^.]+$/, '');

    return {
      id: asset.jobId, // using jobId as local id for simplicity
      projectId: asset.projectId || '',
      jobId: asset.jobId,
      fileName: `${baseName}.${extension}`,
      isReferenced: asset.isReferenced,
      mimeType: `${asset.assetType}/${extension}`,
      size: parseInt(asset.originalSize || '0') || 0,
      status:
        asset.status === 'PROCESSING_COMPLETED' ? 'ready' : asset.status.includes('FAILED') ? 'error' : 'processing',
      gcsUrl: asset.finalUrl || undefined,
      createdAt: typeof asset.createdAt === 'string' ? asset.createdAt : (asset.createdAt as Date)?.toISOString(),
    };
  });

  console.log('[assets] formattedAssets from setAssetsFromDB:', formattedAssets);

  projectAssetsStore.set(formattedAssets);
  updateLoadingState();
}

async function deleteAssets(jobIds: string[]): Promise<boolean> {
  try {
    removePendingAssetJobIds(jobIds);

    const response = await fetch('/api/asset-record', {
      method: 'DELETE',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ jobIds }),
    });
    const data = (await response.json()) as { success: boolean };

    if (data.success) {
      const current = projectAssetsStore.get();
      projectAssetsStore.set(current.filter((a) => !jobIds.includes(a.jobId)));
      return true;
    }

    return false;
  } catch (error) {
    console.error('[assets] Failed to delete asset:', error);
    return false;
  }
}

function updateLoadingState() {
  const assets = projectAssetsStore.get();
  const isLoading = assets.some((a) => a.status === 'uploading' || a.status === 'processing');
  areAssetsLoading.set(isLoading);
}

async function uploadAsset(asset: ProjectAsset, signal: AbortSignal) {
  try {
    const assetType: AssetType = 'image';
    const result = await uploadAssetToGCS(
      asset.file!,
      {
        assetType,
        source: 'asset',
        jobIdParam: asset.jobId,
        projectId: asset.projectId,

        onProgress: (progress) => {
          updateAssetStatus(asset.id, 'uploading', { uploadProgress: progress });
        },
        onStatus: (statusMsg) => {
          // Check if status indicates processing has started
          if (statusMsg.includes('Processing') || statusMsg.includes('processing')) {
            updateAssetStatus(asset.id, 'processing');
          }
        },
      },
      signal,
    );

    if (result.status === 'completed' && result.finalUrl) {
      console.log('[assets] Upload completed successfully:', result);
      updateAssetStatus(asset.id, 'ready', { gcsUrl: result.finalUrl, uploadProgress: 100, jobId: result.jobIdParam });
    } else {
      if (result.error === 'Aborted') {
        throw result.error;
      }

      updateAssetStatus(asset.id, 'error', { error: 'Upload failed' });
    }
  } catch (err) {
    if ((err as string) === 'Aborted') {
      throw new Error('Upload aborted');
    }

    updateAssetStatus(asset.id, 'error', { error: err instanceof Error ? err.message : 'Upload failed' });
  } finally {
    uploadControllers.delete(asset.id);
  }
}

function updateAssetStatus(id: string, status: ProjectAsset['status'], data: Partial<ProjectAsset> = {}) {
  const assets = projectAssetsStore.get();
  projectAssetsStore.set(assets.map((a) => (a.id === id ? { ...a, status, ...data } : a)));
  updateLoadingState();
}
